<template>
  <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
    <header class="flex flex-col gap-2">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h1 class="text-2xl font-semibold tracking-tight">
            Direct Secure Transfer
          </h1>
          <p class="text-sm text-muted-foreground">
            Send one file directly to another authenticated browser. File bytes never pass through Laravel.
          </p>
        </div>
        <Badge v-if="isParticipant" :variant="connectionBadgeVariant" class="gap-1.5">
          <Wifi class="h-3.5 w-3.5" />
          {{ connectionLabel }}
        </Badge>
      </div>
    </header>

    <Alert v-if="errorMessage" variant="destructive">
      <CircleAlert class="h-4 w-4" />
      <AlertTitle>Transfer unavailable</AlertTitle>
      <AlertDescription>{{ errorMessage }}</AlertDescription>
    </Alert>

    <Card v-if="!roomId">
      <CardHeader>
        <CardTitle>Create a private transfer room</CardTitle>
        <CardDescription>The room expires automatically and accepts exactly one recipient.</CardDescription>
      </CardHeader>
      <CardContent>
        <Button :loading="transferStore.isLoading" class="gap-2" @click="handleCreateRoom">
          <Link2 class="h-4 w-4" />
          Create transfer room
        </Button>
      </CardContent>
    </Card>

    <Card v-else-if="transferStore.isLoading && !transferStore.room">
      <CardContent class="flex items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
        <LoaderCircle class="h-5 w-5 animate-spin" />
        Opening secure transfer room…
      </CardContent>
    </Card>

    <Card v-else-if="transferStore.room?.role === 'guest'">
      <CardHeader>
        <CardTitle>Accept file transfer</CardTitle>
        <CardDescription>{{ transferStore.room.creator?.name ?? 'Another user' }} invited you to a browser-to-browser transfer.</CardDescription>
      </CardHeader>
      <CardContent class="flex flex-col gap-4">
        <div class="rounded-lg border bg-muted/40 p-4 text-sm">
          This will reserve the room for you. Only you and the sender will be allowed into its presence channel.
        </div>
        <Button :loading="transferStore.isLoading" class="w-fit gap-2" @click="handleAcceptRoom">
          <ShieldCheck class="h-4 w-4" />
          Accept transfer
        </Button>
      </CardContent>
    </Card>

    <template v-else-if="isParticipant">
      <div class="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <Card>
          <CardHeader>
            <CardTitle>{{ isCreator ? 'Send a file' : 'Receive a file' }}</CardTitle>
            <CardDescription v-if="isCreator">
              Keep this page open after sharing the private room link.
            </CardDescription>
            <CardDescription v-else>
              The sender can start as soon as the peer connection is ready.
            </CardDescription>
          </CardHeader>
          <CardContent class="flex flex-col gap-6">
            <div v-if="isCreator" class="flex flex-col gap-4">
              <label class="flex flex-col gap-2 text-sm">
                <span class="font-medium">Choose file</span>
                <input type="file" class="flex min-h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:cursor-not-allowed disabled:opacity-50" :disabled="isTransferInProgress" @change="handleFileSelection">
              </label>

              <div v-if="selectedFile" class="flex flex-col gap-2 rounded-lg border bg-muted/30 p-4">
                <div class="flex items-center gap-3">
                  <FileUp class="h-5 w-5 shrink-0 text-muted-foreground" />
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium">
                      {{ selectedFile.name }}
                    </p>
                    <p class="text-xs text-muted-foreground">
                      {{ formatBytes(selectedFile.size) }} · modified {{ formatDate(selectedFile.lastModified) }}
                    </p>
                  </div>
                </div>
                <p v-if="selectedFileCanResume" class="text-xs text-amber-700 dark:text-amber-300">
                  This file matches the interrupted transfer and will reuse its stable transfer ID.
                </p>
              </div>

              <div class="flex flex-wrap gap-3">
                <Button :disabled="!selectedFile || !isConnected || isTransferInProgress" class="gap-2" @click="handleStartTransfer">
                  <Send class="h-4 w-4" />
                  {{ selectedFileCanResume ? 'Resume transfer' : 'Start transfer' }}
                </Button>
                <Button v-if="isTransferInProgress" variant="outline" class="gap-2" @click="handleCancelTransfer">
                  <X class="h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </div>

            <div v-else-if="incomingMetadata" class="flex flex-col gap-4">
              <div class="flex items-center gap-3 rounded-lg border bg-muted/30 p-4">
                <FileDown class="h-5 w-5 shrink-0 text-muted-foreground" />
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium">
                    {{ incomingMetadata.fileName }}
                  </p>
                  <p class="text-xs text-muted-foreground">
                    {{ formatBytes(incomingMetadata.size) }} · {{ incomingMetadata.mimeType || 'Unknown file type' }}
                  </p>
                </div>
              </div>
              <Button v-if="downloadAvailable" class="w-fit gap-2" @click="handleSaveFile">
                <Download class="h-4 w-4" />
                Save completed file
              </Button>
            </div>

            <div v-else class="flex min-h-36 flex-col items-center justify-center gap-3 rounded-lg border border-dashed text-center text-sm text-muted-foreground">
              <FileDown class="h-8 w-8" />
              Waiting for file metadata from the sender.
            </div>

            <div v-if="totalBytes > 0" class="flex flex-col gap-3">
              <div class="flex items-center justify-between gap-3 text-sm">
                <span class="font-medium">{{ transferStatusLabel }}</span>
                <span class="tabular-nums text-muted-foreground">{{ progress.toFixed(1) }}%</span>
              </div>
              <div class="h-2.5 overflow-hidden rounded-full bg-muted">
                <div class="h-full rounded-full bg-primary transition-[width] duration-300" :style="{ width: `${progress}%` }" />
              </div>
              <div class="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
                <span>{{ formatBytes(bytesTransferred) }} / {{ formatBytes(totalBytes) }}</span>
                <span>{{ formatBytes(speedBytesPerSecond) }}/s</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div class="flex flex-col gap-6">
          <Card v-if="isCreator">
            <CardHeader>
              <CardTitle class="text-base">
                Private room link
              </CardTitle>
            </CardHeader>
            <CardContent class="flex flex-col gap-3">
              <div class="break-all rounded-md border bg-muted/40 p-3 font-mono text-xs">
                {{ transferStore.room.share_url }}
              </div>
              <Button variant="outline" class="w-fit gap-2" @click="copyShareLink">
                <Copy class="h-4 w-4" />
                Copy link
              </Button>
              <p class="text-xs text-muted-foreground">
                Expires {{ formatDate(transferStore.room.expires_at) }}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle class="text-base">
                Connection details
              </CardTitle>
            </CardHeader>
            <CardContent class="grid gap-4 text-sm">
              <div class="flex items-center justify-between gap-3">
                <span class="text-muted-foreground">Peer</span>
                <span class="font-medium">{{ peerLabel }}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-muted-foreground">Route</span>
                <Badge variant="outline">
                  {{ routeLabel }}
                </Badge>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-muted-foreground">Partial storage</span>
                <span class="font-medium">{{ storageLabel }}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-muted-foreground">Integrity</span>
                <span class="font-medium">{{ integrityLabel }}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-muted-foreground">Encryption</span>
                <span class="font-medium">WebRTC DTLS</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue-sonner'
import { CircleAlert, Copy, Download, FileDown, FileUp, Link2, LoaderCircle, Send, ShieldCheck, Wifi, X } from 'lucide-vue-next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert/index.js'
import { Badge } from '@/components/ui/badge/index.js'
import { Button } from '@/components/ui/button/index.js'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card/index.js'
import { useDirectSecureTransfer } from '../composables/useDirectSecureTransfer.js'

const route = useRoute()
const router = useRouter()
const roomId = computed(() => route.params.room ? String(route.params.room) : null)

const {
  transferStore,
  selectedFile,
  incomingMetadata,
  connectionState,
  transferStatus,
  routeType,
  bytesTransferred,
  totalBytes,
  speedBytesPerSecond,
  progress,
  integrityStatus,
  errorMessage,
  downloadAvailable,
  storageMode,
  selectedFileCanResume,
  isCreator,
  isParticipant,
  isConnected,
  isTransferInProgress,
  initializeRoom,
  createRoom,
  acceptRoom,
  selectFile,
  startTransfer,
  cancelTransfer,
  saveReceivedFile,
  releaseCurrentRoom,
} = useDirectSecureTransfer()

const connectionLabel = computed(() => ({
  idle: 'Idle',
  loading: 'Loading room',
  'waiting-for-acceptance': 'Acceptance required',
  'waiting-for-peer': 'Waiting for peer',
  new: 'Waiting for peer',
  connecting: 'Connecting',
  connected: 'Connected',
  disconnected: 'Disconnected',
  reconnecting: 'Reconnecting',
  failed: 'Connection failed',
  closed: 'Closed',
}[connectionState.value] ?? connectionState.value))

const connectionBadgeVariant = computed(() => {
  if (connectionState.value === 'connected') {
    return 'default'
  }

  if (connectionState.value === 'failed') {
    return 'destructive'
  }

  return 'secondary'
})

const transferStatusLabel = computed(() => ({
  idle: 'Idle',
  ready: 'Ready to send',
  preparing: 'Preparing file fingerprint',
  'waiting-for-receiver': 'Waiting for receiver offset',
  sending: 'Sending',
  receiving: 'Receiving',
  resuming: 'Resuming',
  reconnecting: 'Reconnecting',
  verifying: 'Verifying SHA-256 chain',
  'awaiting-verification': 'Waiting for receiver verification',
  completed: 'Transfer completed',
  cancelled: 'Transfer cancelled',
  failed: 'Transfer failed',
}[transferStatus.value] ?? transferStatus.value))

const routeLabel = computed(() => ({
  direct: 'Direct peer-to-peer',
  relayed: 'TURN relayed',
  unknown: 'Not selected yet',
}[routeType.value] ?? 'Not selected yet'))

const integrityLabel = computed(() => ({
  pending: 'SHA-256 pending',
  verified: 'SHA-256 verified',
  failed: 'Verification failed',
}[integrityStatus.value] ?? integrityStatus.value))

const storageLabel = computed(() => ({
  opfs: 'Origin private file system',
  indexeddb: 'IndexedDB fallback',
}[storageMode.value] ?? 'Not in use'))

const peerLabel = computed(() => {
  const otherPeer = transferStore.peers.find((peer) => peer.role !== transferStore.room?.role)

  if (otherPeer?.name) {
    return otherPeer.name
  }

  return transferStore.peers.length >= 2 ? 'Connected peer' : 'Waiting'
})

const formatBytes = (value) => {
  const bytes = Number(value ?? 0)

  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B'
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const amount = bytes / (1024 ** unitIndex)

  return `${amount.toFixed(unitIndex === 0 ? 0 : amount >= 10 ? 1 : 2)} ${units[unitIndex]}`
}

const formatDate = (value) => {
  const date = typeof value === 'number' ? new Date(value) : new Date(String(value))

  return Number.isNaN(date.getTime()) ? 'unknown' : date.toLocaleString()
}

const loadRouteRoom = async () => {
  if (!roomId.value) {
    await releaseCurrentRoom()
    transferStore.resetRoom()
    return
  }

  try {
    await initializeRoom(roomId.value)
  } catch {
  }
}

const handleCreateRoom = async () => {
  try {
    const room = await createRoom()
    await router.push({
      name: 'DirectSecureTransferRoomPage',
      params: { room: room.public_id },
    })
  } catch {
    toast.error(errorMessage.value)
  }
}

const handleAcceptRoom = async () => {
  try {
    await acceptRoom()
    toast.success('Transfer room accepted.')
  } catch {
    toast.error(errorMessage.value)
  }
}

const copyShareLink = async () => {
  try {
    await navigator.clipboard.writeText(transferStore.room.share_url)
    toast.success('Private transfer link copied.')
  } catch {
    toast.error('Unable to copy the transfer link.')
  }
}

const handleFileSelection = async (event) => {
  const file = event.target?.files?.[0] ?? null
  event.target.value = ''

  try {
    await selectFile(file)
  } catch (error) {
    toast.error(error?.message ?? errorMessage.value)
  }
}

const handleStartTransfer = async () => {
  try {
    await startTransfer()
  } catch (error) {
    toast.error(error?.message ?? 'Unable to start the transfer.')
  }
}

const handleCancelTransfer = async () => {
  await cancelTransfer()
  toast.info('Transfer cancelled. Partial data remains available for resume.')
}

const handleSaveFile = async () => {
  try {
    await saveReceivedFile()
  } catch (error) {
    toast.error(error?.message ?? 'Unable to save the completed file.')
  }
}

onMounted(loadRouteRoom)
watch(roomId, loadRouteRoom)
</script>
